import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { LoadingStatus } from 'src/app/core/domain/events/state_provider';
import { Dietitian } from 'src/app/data/dietitian/domain/dietitian';
import { DietitianStateProvider } from 'src/app/data/dietitian/domain/dietitian_state_provider';
import { EncouragementCommands } from 'src/app/data/dietitian/domain/encouragement/encouragement_commands';
import {
  Notification,
  NotificationProps,
} from 'src/app/data/dietitian/domain/notification/notification';

import { DietitianCommands } from '../../../../data/dietitian/domain/dietitian_commands';
import { NotificationCommands } from '../../../../data/dietitian/domain/notification/notification_commands';
import { PatientGroup } from '../../../../data/dietitian/domain/patient_group/patient_group';
import { PatientGroupCommands } from '../../../../data/dietitian/domain/patient_group/patient_group_commands';
import { ConversationCommands } from '../../../../data/message/domain/conversation/conversation_commands';
import { SortListType } from './questions.service';
import { ScheduledTaskCommands } from '../../../../data/dietitian/domain/scheduled_task/scheduled_task_commands';

export interface QuestionsViewModel {
  isLoading: boolean;
  error: string;
  dietitian: Dietitian;
}

// export class NotificationViewModel {
//   id: string;
//   title: string;
//   message: string;
//   createdAt: Date;
//   readAt?: Date;

//   static fromEntity(notification: Notification) {
//     return {
//       id: notification.notificationId.id.toString(),
//       title: notification.title,
//       message: notification.message,
//       createdAt: notification.createdAt,
//       readAt: notification.readAt,
//     } as NotificationViewModel;
//   }
// }

@Injectable()
export class QuestionsFacade {
  constructor(
    private dietitianStateProvider: DietitianStateProvider,
    private patientGroupCommands: PatientGroupCommands,
    private dietitianCommands: DietitianCommands,
    private conversationCommands: ConversationCommands,
    private notificationCommands: NotificationCommands,
    private dietitianEncouragementCommands: EncouragementCommands,
    private scheduledTaskCommands: ScheduledTaskCommands,
  ) {
    // do nothing
  }

  get questionsStatus$(): Observable<QuestionsViewModel> {
    return this.dietitianStateProvider.state$.pipe(
      map((e) => {
        return <QuestionsViewModel>{
          isLoading: e.loading == LoadingStatus.LOADING,
          error: e.loading == LoadingStatus.ERROR ? e.message : undefined,
          dietitian: e.entity,
        };
      }),
    );
  }

  getCurrentDietitian(): Promise<Dietitian | undefined> {
    return this.dietitianCommands.getCurrentDietitian();
  }

  getDietitianEncouragementCount(dietitianId: string): Promise<number> {
    return this.dietitianEncouragementCommands.getDietitianEncouragementCount(
      dietitianId,
    );
  }

  async getDietitianEncouragementCountByGroup(
    dietitianId: string,
    groupId: string,
  ): Promise<number> {
    return this.dietitianEncouragementCommands.getDietitianEncouragementCountByGroup(
      dietitianId,
      groupId,
    );
  }

  getDietitianScheduledTaskCount(dietitianId: string): Promise<number> {
    return this.scheduledTaskCommands.countScheduledTask(dietitianId);
  }

  getDietitianScheduledTaskCountByGroup(
    dietitianId: string,
    groupId: string,
  ): Promise<number> {
    return this.scheduledTaskCommands.countScheduledTaskByGroup(
      dietitianId,
      groupId,
    );
  }

  getDietitianConversationCount(type?: SortListType, group?: PatientGroup) {
    return this.conversationCommands.findByDietitianIdCount(type, group);
  }

  getDietitianPatientGroups(dietitianId: string): Promise<PatientGroup[]> {
    return this.patientGroupCommands.getDietitianPatientGroups(dietitianId);
  }

  markNotificationAsRead(dietitianId: string, notification: Notification) {
    const notificationProps = {
      ...notification.props,
      readAt: new Date(),
    } as NotificationProps;

    return this.notificationCommands.update(
      dietitianId,
      notification?.notificationId?.id?.toString(),
      notificationProps,
    );
  }

  async getDietitianNotifications(
    dietitianId: string,
  ): Promise<Notification[]> {
    return this.notificationCommands.findDietitianNotifications(dietitianId);
  }

  async countPatients(dietitianId: string | undefined) {
    if (dietitianId) {
      return (await this.dietitianCommands.countPatients(dietitianId))
        .activated;
    }
    return 0;
  }

  async countArchivedPatients(dietitianId: string | undefined) {
    if (dietitianId) {
      return (await this.dietitianCommands.countPatients(dietitianId)).archived;
    }
    return 0;
  }
}
